import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { SCFPrivileges, ScfV2Service } from '../../scf-v2.service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { FormStatusEnum, Roles, NotificationType, FormType, OlogNotificationType, Configuration } from 'src/app/common/enumerations/enumerations';
import { NotificationMessage } from 'src/app/components/catalogs/notifications/services/notification.models';
import { SiteConfigurationService } from 'src/app/components/site-configuration/site-configuration.service';
import { WfSignature, WfSignatureResource, WFTaskSignatureResource } from 'src/app/components/workflow/workflow';
import { SCFMaster } from '../../scf';
import { NotificationService } from 'src/app/components/catalogs/notifications/services/notification.service';
import { BaseComponent } from 'src/app/common/base/base.component';
import { NotificationComponent } from 'src/app/controls/notifications/notification.component';
import { OLogService } from 'src/app/components/olog/olog.service';
import { KEService } from 'src/app/components/kenable/ke.service';
import { Restriction } from '../../../../controls/restrictions/services/restriction';
import { Observable, Subscription, takeUntil } from 'rxjs';
import { NotificationTemplate } from 'src/app/components/catalogs/notifications/services/notification-template';
import { FormViewState } from 'src/app/common/models/form-view-state';
// Kept for the (currently disabled) testSign confirmation popup — see testSign().
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';

@Component({
  selector: 'scf-v2-main-s3',
  templateUrl: './scf-v2-main-s3.component.html',
  styleUrls: ['./scf-v2-main-s3.component.scss']
})
export class ScfV2MainS3Component extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  @Input() scfMaster?: SCFMaster | null;
  @Input() disabled = false;
  @Input() privileges?: SCFPrivileges | null;
  @Input() status?: FormViewState | null;

  @Output() changed = new EventEmitter<SCFMaster>();

  disableControls = true;

  emailsActivated?: boolean;
  amendments!: Amendment[];
  signatures!: WfSignature[];
  wfSignatures?: WfSignatureResource[];

  restrictions!: Restriction[];
  restrictionsFiltered!: Restriction[];
  restrictions$!: Observable<Restriction[]>;
  restrictionsSubs!: Subscription;

  notificationMessage?: NotificationMessage | null;
  notificationTemplates?: NotificationTemplate[];
  notificationTemplates$!: Observable<NotificationTemplate[]>;
  notificationTemplatesSubs!: Subscription;

  constructor(
    private scfService: ScfV2Service,
    private notiService: NotificationService,
    private siteConfigurationService: SiteConfigurationService,
    private ologService: OLogService,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector,
    private keService: KEService
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.siteConfigurationService.getValue(Configuration.SCFNotifications).toPromise().then((data) => {
      this.emailsActivated = data?.value == "True";
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadRestrictions();
    this.loadNotificationTemplates();
  }

  override ngOnDestroy(): void {
    this.restrictionsSubs?.unsubscribe();
    this.notificationTemplatesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  initializeForm() {
    this.formGroup = this.formBuilder.group({
    });
  }

  loadRestrictions() {
    this.restrictions$ = this.store.select((state) => state.Restrictions.data);
    this.restrictionsSubs = this.restrictions$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data?.length) {
        this.restrictions = data;
      }
    });
  }

  refresh() {
    this.setDisabled();
    this.getValues();
  }

  getValues() {
    this.wfSignatures = [];
    this.signatures = [];
    this.amendments = [];
    this.amendments.push({ ID: 0, number: 0 });
    if (this.scfMaster) {
      this.createSignatures(2);
      if (this.scfMaster.scfAmendments?.length && this.scfMaster.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 5)?.wfStepLocals?.[0]?.wfTaskLocals?.[0].wfTasks?.[0]?.signedBy) {
        this.createSignatures(6);
      }
    }
  }

  createSignatures(section: number) {
    let index = 0;
    this.wfSignatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == section)?.wfSignatures?.filter(x => x.code !== 'SUBMIT');
    this.wfSignatures?.map(s => s.sortOrder = (s.amendmentNumber ?? 0) * 1000 + (s.stepOrder ?? 0) * 100 + (s.order ?? 0));
    this.wfSignatures?.sort((a, b) => a.sortOrder - b.sortOrder);

    if (this.wfSignatures) {
      for (const t of this.wfSignatures) {
        const signature: WfSignature = {
          id: t.id,
          approveActionId: t.approveID,
          unapproveActionId: t.unapproveID,
          disapproveActionId: t.disapproveID,
          amendmentId: t.amendmentID,
          amendmentNumber: t.amendmentNumber ? t.amendmentNumber : 0,
          code: t.code,
          name: t.titleBefore,
          roles: t.roles.map(r => r?.id),
          roleCodes: t.roles.map(r => r?.code).join('/'),
          userNames: t.users?.map(r => r?.name)[0],
          disabled: (t.disabled || this.disabled),
          testSignReady: t.testSignReady,
          approvedBy: t.signedBy,
          approved: t.signed,
          approvedOn: t.signedOn,
          approveAction: t.approveEnabled && !this.disabled && t.approveID !== 0,
          unapproveAction: t.unapproveEnabled && !this.disabled && t.unapproveID !== 0,
          disapproveAction: t.disapproveEnabled && !this.disabled && t.disapproveID !== 0,
          amendment: t.amendmentEnabled,
          required: t.required,
          taskId: t.taskID,
          value: t.value,
          warning: t.warning,
          requesterSignature: t.requesterSignature,
          visible: t.visible,
          enabled: t.enabled,
          sectionNumber: section
        };
        // if (!signature.enabled) {
        //   signature.approvedBy = null;
        //   signature.approved = false;
        // }
        index++;
        if (t.amendmentNumber && !this.amendments.map(x => x.number).includes(t.amendmentNumber)) {
          this.amendments.push({ ID: t.amendmentID, number: t.amendmentNumber });
          this.amendments = this.amendments.sort((a, b) => a.number - b.number);
        }
        this.signatures.push(signature);
      }
    }
  }

  signCount() {
    let count = 0;
    if (this.wfSignatures) {
      this.wfSignatures.map(t => {
        if (t?.signed) { count++; }
      });
    }
    return count;
  }

  setValues() {

  }

  setDisabled() {
    this.disabled =
      (this.scfMaster?.status !== FormStatusEnum.WorkApprovals &&
        this.scfMaster?.status !== FormStatusEnum.AmendmentPending &&
        this.scfMaster?.status !== FormStatusEnum.PendingActivation &&
        this.scfMaster?.status !== FormStatusEnum.AmendmentPending) ||
      this.getCurrentUser() === undefined;
  }


  getSignatures(amendment: number) {
    return this.signatures?.filter(x => x.amendmentNumber == amendment);
  }

  getLastAmendment() {
    const last = this.signatures.filter(x => x.amendmentNumber).map(x => x.amendmentNumber).sort((a, b) => (a ?? 0) - (b ?? 0)).reverse();
    return last ? last[0] : 0;
  }

  /**
   * Whether signing `s` would be the final outstanding signature in its section — the one that
   * activates the form. Mirrors the original condition in approve(): no other enabled, unsigned
   * sibling signature remains for the same amendment, and `s` isn't one of the first two
   * signatures (index > 1). Extracted so testSign() can run the same activation routine.
   */
  private isFinalSectionSignature(s: WfSignature): boolean {
    const wfSignatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == s.sectionNumber)?.wfSignatures?.filter(x => x.code !== 'SUBMIT');
    const index = wfSignatures?.findIndex(x => x.id == s.id);
    const signaturesToSign = wfSignatures?.filter(x => x.enabled && (x.amendmentNumber ? x.amendmentNumber : 0) == s.amendmentNumber && x.taskID != s.taskId && !x.signed);
    return signaturesToSign?.length == 0 && index != null && index > 1;
  }

  /**
   * Posts the Activate (or Amended) OLog for the form and refreshes the affected KEs — the side
   * effects approve() runs when the final Work Approvals signature activates the form. Returns
   * whether it is OK to proceed with the signature (false only if the OLog post failed and the
   * user declined to continue). Shared by approve() and the tester testSign().
   */
  private async runActivationOlog(s: WfSignature): Promise<boolean> {
    if (!this.scfMaster?.id) return true;
    // Restriction IDs in the SCF master -> their resource IDs, for the KE refresh.
    const restrictionIDsInMaster = this.scfMaster?.scfRestrictionsV2?.map(r => r.restrictionID).filter(id => id !== undefined) ?? [];
    const restrictedResourceIDs = this.restrictions
      .filter(r => restrictionIDsInMaster.includes(r.id ?? 0))
      .map(r => r.resourceID)
      .filter((id): id is number => id !== undefined);

    const isAmendment = this.scfMaster?.status === FormStatusEnum.AmendmentPending;
    const result = await this.ologService.postToOlog(
      FormType.SCF,
      this.scfMaster.id,
      isAmendment ? OlogNotificationType.Amended : OlogNotificationType.Activate,
      s.amendmentNumber
    );
    const ok = result?.ok ?? false;

    // Refresh KEs
    if (restrictedResourceIDs?.length) {
      this.keService.processKES(restrictedResourceIDs);
    }
    return ok;
  }

  async approve(s: WfSignature) {
    let ok = true;
    if (this.isFinalSectionSignature(s)) {
      ok = await this.showAproveToActiveMessage();
      if (ok && this.scfMaster?.id) {
        ok = await this.runActivationOlog(s);
      }
      else { this.getValues(); }
    }
    if (ok) {
      // //this.loading.emit(true);
      const wfTaskSignature: WFTaskSignatureResource = {
        taskId: s.taskId,
        actionId: s.approveActionId
      };
      this.scfService.sign(wfTaskSignature).toPromise().then(data => {
        this.scfMaster = data;
        this.checkAndSendNotification(this.scfMaster!, s.amendmentNumber);
      },
        (error) => {
          console.log(error);
          this.alert.defaultError();
        }
      );
    }
    else {
      this.getValues();
    }
  }

  /**
   * Whether the tester-only "auto-sign as role" tooling should be shown.
   * Gated defensively: only on non-production builds AND for System Administrators,
   * mirroring scf-v2-main-s1's showTestTools so it can never surface in production.
   */
  get showTestTools(): boolean {
    return !this.environmentService.isProduction() && this.isSA() && this.testerToolsService.enabled;
  }

  /**
   * Tester convenience: whether to offer the "Tester Approve" button for a signature.
   *
   * Applies to the Work Approvals signatures (section 2) AND the amendment approval signatures
   * (section 6) — the same approvals that re-activate a submitted amendment. The button appears on
   * the one signature whose turn it currently is — the backend sets `testSignReady` (a
   * role/user-independent readiness flag computed from the workflow order, so it correctly excludes
   * signatures that aren't reachable yet, e.g. the Op Restrictions in Place signatures while the
   * Requester is still pending, and only the active amendment group) — but only when the logged-in
   * SA cannot actually complete the approval themselves. "Cannot complete it" covers both cases:
   * the SA lacks the role (no Approve button at all), AND the SA holds the role but the real
   * Approve button is greyed out (e.g. they submitted the form/amendment). The server-side TestSign
   * resolves the appropriate signer in either case.
   *
   * No amendment-number filter is needed: `testSignReady` is only set for the active group
   * (section 2, or the latest amendment in section 6), so older/already-signed signatures never
   * qualify.
   *
   * The button hides only when the SA can genuinely sign it themselves — i.e. the real Approve
   * button is both shown (`approveAction`) and enabled (`!disabled`) — so a super admin who
   * also holds the signature's role just uses the normal Approve button.
   */
  canTestSign(s: WfSignature): boolean {
    return (
      this.showTestTools &&
      (s.sectionNumber === 2 || s.sectionNumber === 6) &&
      !!s.testSignReady &&
      !this.disabled &&
      !!s.approveActionId &&
      !(s.approveAction && !s.disabled)
    );
  }

  /**
   * Tester tooling (non-production): applies this signature on behalf of the appropriate user
   * (the first holder of one of the signature's roles, or the pinned user such as the Requester)
   * via the non-prod, SA-gated TestSign endpoint. The signer is resolved server-side (role-based
   * signers exclude anyone who already signed in this workflow, including the submitter). The
   * SignalR refresh that follows reloads the form for all open clients. Signing the final
   * outstanding signature in the section advances the form to the next status.
   */
  async testSign(s: WfSignature) {
    if (!this.canTestSign(s)) return;
    // Confirmation popup intentionally disabled for now — the tooltip on the
    // "Tester Approve" button plus the non-prod/SA guards are sufficient. Kept
    // (commented) so it can be re-enabled easily if we want the prompt back.
    // const confirm = this.dialog.open(YesNoDialogComponent, {
    //   width: '450px',
    //   data: {
    //     message:
    //       'Tester tool (non-production): apply the "' +
    //       (s.name ?? 'this') +
    //       '" signature as the appropriate user (' +
    //       (s.roleCodes || 'Requester') +
    //       ')? This signs the form as a different user.',
    //   },
    // });
    // const ok = await confirm.afterClosed().toPromise();
    // if (!ok) return;

    // When this is the final signature (which activates the form), run the same OLog + KE
    // refresh routine approve() runs, so the tester flow produces the real side effects. The
    // production "confirm activation" prompt is skipped — the tester explicitly chose to advance.
    if (this.isFinalSectionSignature(s) && this.scfMaster?.id) {
      const ok = await this.runActivationOlog(s);
      if (!ok) {
        this.getValues();
        return;
      }
    }

    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.approveActionId,
    };
    this.scfService.testSign(wfTaskSignature).toPromise().then(
      (data) => {
        this.scfMaster = data;
        this.checkAndSendNotification(this.scfMaster!, s.amendmentNumber);
      },
      (error) => {
        console.log(error);
        this.alert.defaultError();
      }
    );
  }

  async unapprove(s: WfSignature) {

    let verificator = true;
    if (this.scfMaster?.requesterId === this.getCurrentUser()?.id) {
      if (this.signatures.some(s => s.requesterSignature)) {
        try {
          verificator = false;
          let signatures: WfSignatureResource[] | undefined = [];
          if (!this.status?.isViewingAmendment) {
            signatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number === 2)?.wfSignatures;
          } else if (this.status.isViewingAmendment) {
            signatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals.find(s => s.number === 6)?.wfSignatures;
            signatures = signatures?.filter(x => x.amendmentID === s.amendmentId);
          }
          const requesterTask = signatures?.find(s => s.requesterSignature);
          const index = signatures?.findIndex(x => x.id === s.id);
          const validate =
            requesterTask &&
            requesterTask.unapproveID &&
            requesterTask.signed &&
            index !== undefined &&
            index !== null &&
            (index === 1 || (this.status?.isViewingAmendment && index === 0));
          if (validate) {
            const wfTaskSignature: WFTaskSignatureResource = {
              taskId: requesterTask.taskID,
              actionId: requesterTask.unapproveID
            };
            await this.scfService.sign(wfTaskSignature).toPromise().then(() => {
            });
          }
          verificator = true;
        } catch (error) {
          this.alert.defaultError();
        }
      }
    }
    if (verificator) {
      const wfTaskSignature: WFTaskSignatureResource = {
        taskId: s.taskId,
        actionId: s.unapproveActionId
      };
      this.scfService.sign(wfTaskSignature).toPromise().then(() => {
      },
        (error) => {
          console.log(error);
          this.alert.defaultError();
        }
      );
    }
  }

  loadNotificationTemplates() {
    this.notificationTemplates$ = this.store.select(state => state.NotificationTemplates.data);
    this.notificationTemplatesSubs = this.notificationTemplates$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data?.length)
        this.notificationTemplates = data;
    });
  }

  checkAndSendNotification(scfMaster: SCFMaster, amendmentNumber?: number) {
    const wfSignatures = scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 2)?.wfSignatures?.filter(x => x.code !== 'SUBMIT' && (x.amendmentNumber ? x.amendmentNumber : 0) == amendmentNumber);
    const nextSignature = wfSignatures?.find(x => x.enabled && !x.signed);
    const lastSignatureSigned = wfSignatures
      ?.slice() // Creates a shallow copy to avoid mutating the original array
      .reverse().find(x => x.enabled && x.signed);

    if (nextSignature?.requesterSignature) {
      //TODO Notify to Requester if Required
      this.sendNotification(scfMaster, NotificationType.RequesterRequired, nextSignature);
    }
    else if (lastSignatureSigned?.requesterSignature || ((lastSignatureSigned?.code == 'RP' || lastSignatureSigned?.code == 'RWAPI') && (nextSignature?.code == 'AOFO' || nextSignature?.code == 'OIC'))) {
      //TODO Notify to AOFO/OIC
      this.sendNotification(scfMaster, NotificationType.RequesterSigned, nextSignature);
    }
  }

  async sendNotification(scfMaster: SCFMaster, type: NotificationType, signature?: WfSignatureResource) {
    const toAddresses = type == NotificationType.RequesterRequired ? signature?.users?.map(u => `${u.name} <${u.emailAddress}>`) : this.getEmailAddressesFromRoles([Roles.AO, Roles.FO, Roles.OIC]);
    const template = this.notificationTemplates?.find(t => t.type == type);
    const notification = {
      serialNo: scfMaster.serialNo,
      notificationId: 0,
      documentId: scfMaster.id,
      toAddresses: JSON.stringify({ computed: toAddresses }),
      messageSubject: template?.messageSubject.replace('{serialNo}', scfMaster.serialNo),
      messageBody: template?.messageBody.replace('{serialNo}', scfMaster.serialNo),
      type: template?.id,
      code: '',
      messageAdditionalText: '',
      signature: template?.signature,
    } as NotificationMessage;

    await this.notiService.createNotification(notification).toPromise().then(async data => {
      if (data) {
        const addressesObject = this.utils.JSONparse(data.toAddresses);
        const emailAddreses = (addressesObject.computed as string[]).concat(addressesObject.added);
        this.notiService.sendNotification(data).toPromise().then(() => {
          this.alert.success(emailAddreses.join('<br>'), 'Email Notification Sent to:', 10000);
        });
      }
    }).catch(error => {
      console.log(error);
      this.alert.error('There was a problem sending notification!', 'Notification Error!', 10000);
    });
  }

  openNotificationDialog(data: NotificationMessage, emailAddresses?: string[], readOnly?: boolean) {
    const dialogRef = this.dialog.open(NotificationComponent, {
      height: "fit-content",
      width: "60vw",
      data: {
        message: data,
        emailAddresses,
        readOnly,
        confirmation: true,
        type: data.type
      },
    });

    return dialogRef.afterClosed().toPromise();
  }

  disapprove(s: WfSignature, data: any) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.disapproveActionId,
      reason: data.text
    };

    this.scfService.sign(wfTaskSignature).toPromise().catch((error) => {
      console.log(error);
      this.alert.defaultError();
    });
  }
}

export class Amendment {
  ID!: number;
  number!: number;
}
